import React from 'react';

interface BookSection {
  id: string;
  name: string;
  title?: string;
  order: number;
  type: 'chapter' | 'file' | 'content' | 'text' | 'image' | 'pdf' | 'video' | 'audio' | 'interactive' | 'quiz';
  duration: number;
  start_day_offset: number;
  start_day_offset_relative_to: 'book' | 'chapter' | 'section';
  start_day_offset_reference_id?: string;
  file_id?: string;
  file?: {
    file_id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    // Prefer backend-provided view_url for any viewing/preview, fall back to file_url for legacy data
    view_url?: string;
    file_url?: string;
    file_path?: string; // backend-internal; do not use to build URLs on FE
  };
  content_block?: {
    content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'text' | 'list' | 'flashcard';
    payload: any;
  };
  children?: BookSection[];
}

interface BookContentDisplayProps {
  contents: BookSection[];
  onToggleModule: (moduleId: string) => void;
  onToggleSection: (sectionId: string) => void;
  expandedModules: Set<string>;
  expandedSections: Set<string>;
  getDeliveryMethodStyle: (method: string) => string;
  getDeliveryMethodIcon: (method: string) => React.ReactNode;
  getModuleTypeLabel: (method: string) => string;
  getDeliveryMethodLabel: (method: string) => string;
}

interface ChapterDisplayProps {
  chapter: BookSection;
  chapterIndex: number;
  onToggleModule: (moduleId: string) => void;
  onToggleSection: (sectionId: string) => void;
  expandedModules: Set<string>;
  expandedSections: Set<string>;
  getDeliveryMethodStyle: (method: string) => string;
  getDeliveryMethodIcon: (method: string) => React.ReactNode;
  getModuleTypeLabel: (method: string) => string;
  getDeliveryMethodLabel: (method: string) => string;
}

interface SectionDisplayProps {
  section: BookSection;
  sectionIndex: number;
  onToggleSection: (sectionId: string) => void;
  expandedSections: Set<string>;
  getDeliveryMethodStyle: (method: string) => string;
  getDeliveryMethodIcon: (method: string) => React.ReactNode;
  getDeliveryMethodLabel: (method: string) => string;
}

// Chapter Display Component
function ChapterDisplay({
  chapter,
  chapterIndex,
  onToggleModule,
  onToggleSection,
  expandedModules,
  expandedSections,
  getDeliveryMethodStyle,
  getDeliveryMethodIcon,
  getModuleTypeLabel,
  getDeliveryMethodLabel,
}: ChapterDisplayProps) {
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-800">
      <div className="flex items-center justify-between bg-gray-50 px-4 py-3 dark:bg-gray-800">
        {/* Chapter header */}
        <div className="flex items-center">
          <span
            className="mr-2 text-gray-500 cursor-pointer hover:text-gray-700"
            onClick={() => onToggleModule(chapter.id)}
          >
            {expandedModules.has(chapter.id) ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6"></path>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6"></path>
              </svg>
            )}
          </span>
          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
            <div className="font-medium mr-3">{chapter.title || chapter.name}</div>
            <div
              className={`flex items-center text-xs font-medium px-2 py-1 rounded-md border ${getDeliveryMethodStyle(
                chapter.type
              )}`}
            >
              {getDeliveryMethodIcon(chapter.type)}
              {getModuleTypeLabel(chapter.type)}
            </div>
          </div>
        </div>
      </div>
      {expandedModules.has(chapter.id) && (
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {/* Render sections structure */}
          {chapter.children &&
            chapter.children.map((section, sectionIndex) => (
              <SectionDisplay
                key={section.id}
                section={section}
                sectionIndex={sectionIndex}
                onToggleSection={onToggleSection}
                expandedSections={expandedSections}
                getDeliveryMethodStyle={getDeliveryMethodStyle}
                getDeliveryMethodIcon={getDeliveryMethodIcon}
                getDeliveryMethodLabel={getDeliveryMethodLabel}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// Section Display Component
function SectionDisplay({
  section,
  sectionIndex,
  onToggleSection,
  expandedSections,
  getDeliveryMethodStyle,
  getDeliveryMethodIcon,
  getDeliveryMethodLabel,
}: SectionDisplayProps) {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-2 rounded-md p-2 -m-2">
        <div className="flex items-center">
          <span
            className="mr-2 text-gray-500 cursor-pointer hover:text-gray-700"
            onClick={() => onToggleSection(section.id)}
          >
            {expandedSections.has(section.id) ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6"></path>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6"></path>
              </svg>
            )}
          </span>
          <div
            className="flex items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mr-3">
              {section.title || section.name}
            </h4>
            <div
              className={`flex items-center text-xs font-medium px-3 py-1 rounded-full border ${getDeliveryMethodStyle(
                section.type
              )}`}
            >
              {getDeliveryMethodIcon(section.type)}
              {getDeliveryMethodLabel(section.type)}
            </div>
          </div>
        </div>
      </div>
      {expandedSections.has(section.id) && (
        <div className="space-y-2 ml-4">
          {/* Show quiz content for quiz sections */}
          {section.type === 'quiz' && section.content_block?.content_type === 'quiz' ? (
            <div className="flex items-center justify-between px-4 py-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center">
                <span className="mr-2 text-yellow-600 dark:text-yellow-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18"></path>
                    <path d="M7 12h10"></path>
                    <path d="M7 18h6"></path>
                    <path d="M3 6v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6"></path>
                    <path d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2"></path>
                  </svg>
                </span>
                <div className="flex-1">
                  <div className="font-medium text-yellow-800 dark:text-yellow-200">
                    Bài kiểm tra
                  </div>
                  <div className="text-sm text-yellow-600 dark:text-yellow-400">
                    {(section.content_block?.payload?.questions?.length || 0)} câu hỏi
                    {section.content_block?.payload?.time_limit_minutes && 
                      ` • ${section.content_block.payload.time_limit_minutes} phút`}
                    {section.content_block?.payload?.passing_score && 
                      ` • Điểm đạt: ${section.content_block.payload.passing_score}%`}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Render nested children recursively */
            <div className="space-y-2">
              {/* If section has children, render them recursively */}
              {section.children && section.children.length > 0 ? (
                section.children.map((child, childIndex) => (
                  <SectionDisplay
                    key={child.id}
                    section={child}
                    sectionIndex={childIndex}
                    onToggleSection={onToggleSection}
                    expandedSections={expandedSections}
                    getDeliveryMethodStyle={getDeliveryMethodStyle}
                    getDeliveryMethodIcon={getDeliveryMethodIcon}
                    getDeliveryMethodLabel={getDeliveryMethodLabel}
                  />
                ))
              ) : (
                /* Show file content for leaf sections */
                <div className="flex items-center justify-between px-4 py-3 rounded-md bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center">
                    <span className="mr-2 text-gray-500">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                      </svg>
                    </span>
                    <div className="flex-1">
                      <div className="font-medium">
                        {section.file?.file_name || section.title || section.name || "Tài liệu"}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {section.file?.file_type?.toUpperCase() || "FILE"}
                        {section.file?.file_size &&
                          ` • ${(section.file.file_size / 1024 / 1024).toFixed(
                            1
                          )} MB`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {(section.file?.view_url || section.file?.file_url) && (
                      <a
                        href={section.file.view_url || section.file.file_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <span className="inline-flex items-center px-3 py-1 rounded-md bg-blue-100 text-blue-800 text-sm hover:bg-blue-200 transition-colors">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="mr-1"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                          Tải xuống
                        </span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export function BookContentDisplay({
  contents,
  onToggleModule,
  onToggleSection,
  expandedModules,
  expandedSections,
  getDeliveryMethodStyle,
  getDeliveryMethodIcon,
  getModuleTypeLabel,
  getDeliveryMethodLabel,
}: BookContentDisplayProps) {

  if (contents.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-500 dark:text-gray-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mx-auto mb-4"
          >
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Chưa có chương nào
          </h3>
          <p className="text-sm">
            Sách này chưa có chương nào. Bấm chỉnh sửa để bắt đầu.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {contents.map((chapter, chapterIndex) => (
        <ChapterDisplay
          key={chapter.id}
          chapter={chapter}
          chapterIndex={chapterIndex}
          onToggleModule={onToggleModule}
          onToggleSection={onToggleSection}
          expandedModules={expandedModules}
          expandedSections={expandedSections}
          getDeliveryMethodStyle={getDeliveryMethodStyle}
          getDeliveryMethodIcon={getDeliveryMethodIcon}
          getModuleTypeLabel={getModuleTypeLabel}
          getDeliveryMethodLabel={getDeliveryMethodLabel}
        />
      ))}
    </div>
  );
}
